/* eslint-disable max-lines -- presentation mode owns the whole audience-window UI; splitting fragments the state machine */
/**
 * PresentationMode
 *
 * Full-window audience overlay for the Slide Sense builder — PowerPoint
 * F5-equivalent.  Renders the current slide as a full-bleed image and
 * drives navigation via keyboard and click events.
 *
 * Fullscreen lifecycle:
 *   - Entering fullscreen MUST happen in the same user-gesture tick as
 *     the click that opens the mode. `SlideSensePage` owns the entry —
 *     it calls `requestFullscreen()` on a persistent portal host *inside
 *     the click handler* (synchronously, before setState) and then
 *     renders us into that host via `createPortal`. Doing it here (from
 *     a useLayoutEffect on mount) is too late — the browser has already
 *     ended the click task by the time React commits, and Chrome/Safari
 *     reject the call with "API can only be initiated by a user
 *     gesture." We handle graceful exit on unmount AND listen for
 *     `fullscreenchange` so the overlay closes when the user exits
 *     fullscreen via browser chrome / Esc / F11.
 *
 * Image loading:
 *   - `/api/slide-sense/slides/{id}/image` streams the PNG, but the
 *     Bearer token doesn't ride on plain `<img src>` resource loads in
 *     local dev (they 401). We load through `useSlideImageBlob` (axios +
 *     auth → cached blob URL) and warm the next two slides via
 *     `prefetchSlideImageBlob`. (#2153)
 *
 * Controls (see `usePresentationKeyboard`):
 *   - Arrow keys, Space, PageUp/Dn, n/p, Enter: navigation
 *   - Home/End: jump to first/last
 *   - Esc / F: exit
 *   - B: toggle black "blank" state
 *   - Digits + Enter: jump to slide N (1-indexed)
 *   - Click anywhere on the overlay: next slide
 *
 * On-screen overlay (Task 2):
 *   - Bottom toolbar with Prev / counter / Next / Exit buttons.  The
 *     toolbar auto-hides after 2s of no mouse movement and re-appears
 *     on `mousemove`.  Button clicks stopPropagation so they don't
 *     trigger the container's click-to-next.
 *   - First-run hint (see `slideSense.present.exitHint`) shown at the
 *     top for 3s on mount, then fades out.  Uses `pointer-events-none`
 *     so it never blocks clicks.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { BuilderSlide, SlideHotspot } from "../../types"

import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Pen,
  Pointer,
  Trash2,
  Undo2,
  X,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

import { AnnotationCanvas, type Stroke } from "./AnnotationCanvas"
import { LaserPointer } from "./LaserPointer"
import { useImageAspect } from "./slide-stage-utils"
import { SlideLinkHotspots } from "./SlideLinkHotspots"
import { SlideStage, StageLaserDot } from "./SlideStage"
import { ThumbnailStrip } from "./ThumbnailStrip"
import {
  type PresentationSyncApi,
  type PresentationSyncMode,
  usePresentationSync,
} from "./use-presentation-sync"
import { usePresentationKeyboard } from "./usePresentationKeyboard"
import { prefetchSlideImageBlob, useSlideImageBlob } from "../../hooks/use-slide-image-blob"
import {
  coerceLinearStartIndex,
  firstLinearIndex,
  lastLinearIndex,
  nextLinearIndex,
  prevLinearIndex,
  resolveHotspotJump,
} from "../../utils/presentation-nav-utils"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PresentationModeProps {
  slides: BuilderSlide[]
  onExit: () => void
  /** Index to open on (defaults to 0). */
  initialIndex?: number
  /**
   * When set, this presentation is the "master" side of a dual-window
   * session — opens a BroadcastChannel, publishes state changes, and
   * responds to `jump` messages from the presenter window. `null` /
   * `undefined` disables sync entirely (single-window flow).
   */
  sessionId?: string | null
  /**
   * Optional pre-built sync API. When provided (mostly for tests),
   * `sessionId` is ignored — the component attaches listeners to this
   * API instead of opening its own channel.
   */
  sync?: PresentationSyncApi
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const OVERLAY_HIDE_DELAY_MS = 2000
const HINT_HIDE_DELAY_MS = 3000

type PresentationDrawMode = "nav" | "laser" | "pen"

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PresentationMode({
  slides: allSlides,
  onExit,
  initialIndex = 0,
  sessionId = null,
  sync: syncOverride,
}: PresentationModeProps) {
  const { t } = useTranslation()
  // Keep source-hidden slides in the deck: Download force-shows them, so
  // Preview linear next/prev walk builder order (matching PowerPoint of
  // that file). In-deck hyperlinks can still jump to any slide.
  const slides = allSlides
  const safeInitial = slides.length === 0 ? 0 : coerceLinearStartIndex(slides, initialIndex)
  const [currentIndex, setCurrentIndex] = useState(safeInitial)
  const [isBlank, setIsBlank] = useState(false)
  const [isOverlayVisible, setIsOverlayVisible] = useState(true)
  const [isHintVisible, setIsHintVisible] = useState(true)
  const [showThumbs, setShowThumbs] = useState(false)
  const [mode, setMode] = useState<PresentationDrawMode>("nav")
  const [strokesBySlide, setStrokesBySlide] = useState<Map<string, Stroke[]>>(() => new Map())
  const [isPresenterClosed, setIsPresenterClosed] = useState(false)
  // Laser dot driven by the presenter window (normalized stage coords).
  // `null` = no remote laser. Only used on the dual-window path (#2153).
  const [remoteLaser, setRemoteLaser] = useState<{ x: number; y: number } | null>(null)
  // Slide-image aspect ratio, so the stage matches the real slide shape
  // and remote pointer/strokes line up with the presenter window.
  const { aspect, onImgLoad } = useImageAspect()

  // Open (or inherit) a BroadcastChannel for dual-window sync. The hook
  // is a no-op when `sessionId` is null — in which case publish/subscribe
  // just swallow messages, so the rest of the component can stay
  // unconditional.
  const ownSync = usePresentationSync(syncOverride ? null : sessionId)
  const sync = syncOverride ?? ownSync
  // Ref to the overlay root — kept around for focus management and
  // potential future use. Fullscreen entry is owned by SlideSensePage
  // (it requests fullscreen on the portal host synchronously inside the
  // click handler before we mount), so we no longer call
  // requestFullscreen from this component.
  const rootRef = useRef<HTMLDivElement | null>(null)
  const overlayHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hintHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Timestamp of the most recent Esc-driven mode downgrade (pen/laser → nav).
  // Used to suppress the `fullscreenchange` exit handler when the browser
  // also consumes the same Esc press to leave native fullscreen — otherwise
  // the 2-step Esc priority would be bypassed.
  const escDowngradeAtRef = useRef(0)

  const lastViewedIndexRef = useRef<number | null>(null)

  // ---- Navigation --------------------------------------------------------

  const goNext = useCallback(() => {
    setIsBlank(false)
    setCurrentIndex((i) => nextLinearIndex(slides, i))
  }, [slides])

  const goPrev = useCallback(() => {
    setIsBlank(false)
    setCurrentIndex((i) => prevLinearIndex(slides, i))
  }, [slides])

  const goHome = useCallback(() => {
    setIsBlank(false)
    setCurrentIndex(firstLinearIndex(slides))
  }, [slides])

  const goEnd = useCallback(() => {
    setIsBlank(false)
    setCurrentIndex(lastLinearIndex(slides))
  }, [slides])

  const toggleBlank = useCallback(() => {
    setIsBlank((b) => !b)
  }, [])

  const jumpTo = useCallback(
    (slideNumber: number) => {
      if (slides.length === 0) return
      const zeroBased = Math.max(0, Math.min(slideNumber - 1, slides.length - 1))
      setIsBlank(false)
      setCurrentIndex(zeroBased)
    },
    [slides.length]
  )

  // 0-indexed variant used by the thumbnail strip — avoids the
  // off-by-one the 1-indexed `jumpTo` does for keyboard digit flows.
  const jumpToIndex = useCallback(
    (index: number) => {
      if (slides.length === 0) return
      const clamped = Math.max(0, Math.min(index, slides.length - 1))
      setIsBlank(false)
      setCurrentIndex(clamped)
    },
    [slides.length]
  )

  const activateHotspot = useCallback(
    (hotspot: SlideHotspot) => {
      const target = resolveHotspotJump(slides, hotspot, {
        currentIndex,
        lastViewedIndex: lastViewedIndexRef.current,
      })
      if (target === null || target === currentIndex) return
      if (hotspot.kind === "slide") lastViewedIndexRef.current = currentIndex
      setIsBlank(false)
      setCurrentIndex(target)
    },
    [currentIndex, slides]
  )

  const toggleThumbs = useCallback(() => {
    setShowThumbs((v) => !v)
  }, [])

  // ---- Draw modes (laser / pen) -----------------------------------------
  //
  // The presenter can be in one of three modes at a time: "nav" (default,
  // click-to-advance + arrow-key nav), "laser" (cursor hidden, a red
  // glowing dot follows the mouse), or "pen" (canvas overlay captures
  // drags as freehand strokes).  Toggling the active mode returns to
  // nav; toggling the other one switches into that mode.

  const toggleLaser = useCallback(() => {
    setMode((m) => (m === "laser" ? "nav" : "laser"))
  }, [])

  const togglePen = useCallback(() => {
    setMode((m) => (m === "pen" ? "nav" : "pen"))
  }, [])

  // ---- Per-slide annotation store ---------------------------------------

  const currentSlideId = slides[currentIndex]?.slide.id
  const currentStrokes = useMemo<Stroke[]>(
    () => (currentSlideId ? (strokesBySlide.get(currentSlideId) ?? []) : []),
    [currentSlideId, strokesBySlide]
  )

  const addStroke = useCallback(
    (stroke: Stroke) => {
      if (!currentSlideId) return
      setStrokesBySlide((prev) => {
        const next = new Map(prev)
        const existing = next.get(currentSlideId) ?? []
        next.set(currentSlideId, [...existing, stroke])
        return next
      })
    },
    [currentSlideId]
  )

  const clearCurrentStrokes = useCallback(() => {
    if (!currentSlideId) return
    setStrokesBySlide((prev) => {
      if (!prev.has(currentSlideId)) return prev
      const next = new Map(prev)
      next.delete(currentSlideId)
      return next
    })
  }, [currentSlideId])

  // LIFO per-slide undo. PowerPoint's pen has no redo; this matches
  // that mental model — pop the most recent stroke off the current
  // slide's stack. No-op when the stack is empty.
  const undoLastStroke = useCallback(() => {
    if (!currentSlideId) return
    setStrokesBySlide((prev) => {
      const existing = prev.get(currentSlideId) ?? []
      if (existing.length === 0) return prev
      const next = new Map(prev)
      next.set(currentSlideId, existing.slice(0, -1))
      return next
    })
  }, [currentSlideId])

  // Keyboard-only wrapper for the Undo action — same gate as
  // handleClearStrokes: U / Ctrl+Z should be a no-op outside pen mode
  // so they don't silently mutate annotations during navigation.
  const handleUndoStroke = useCallback(() => {
    if (mode !== "pen") return
    undoLastStroke()
  }, [mode, undoLastStroke])

  // Slide-targeted stroke mutations — used to apply pen events streamed
  // from the presenter window, which may target any slide id (not just
  // the one currently shown), e.g. a stroke that arrives a tick before
  // the matching `state` navigation.
  const addStrokeForSlide = useCallback((slideId: string, stroke: Stroke) => {
    setStrokesBySlide((prev) => {
      const next = new Map(prev)
      const existing = next.get(slideId) ?? []
      next.set(slideId, [...existing, stroke])
      return next
    })
  }, [])

  const clearStrokesForSlide = useCallback((slideId: string) => {
    setStrokesBySlide((prev) => {
      if (!prev.has(slideId)) return prev
      const next = new Map(prev)
      next.delete(slideId)
      return next
    })
  }, [])

  const undoStrokeForSlide = useCallback((slideId: string) => {
    setStrokesBySlide((prev) => {
      const existing = prev.get(slideId) ?? []
      if (existing.length === 0) return prev
      const next = new Map(prev)
      next.set(slideId, existing.slice(0, -1))
      return next
    })
  }, [])

  // Keyboard-only wrapper for the Clear action. The `E` key should be a
  // no-op outside pen mode — otherwise pressing E during nav silently wipes
  // the current slide's annotations (destructive foot-gun, not
  // spec-compliant with PowerPoint). The toolbar Clear button is hidden
  // outside pen mode (see Render section below) so it doesn't need this
  // guard.
  const handleClearStrokes = useCallback(() => {
    if (mode !== "pen") return
    clearCurrentStrokes()
  }, [mode, clearCurrentStrokes])

  // ---- Esc priority -----------------------------------------------------
  //
  // When the presenter is in pen/laser mode, the first Esc drops back to
  // nav-mode and only a *second* Esc actually exits the presentation.
  // We wrap the parent-supplied `onExit` here and pass the wrapper down
  // to the keyboard hook.
  const handleExitKey = useCallback(() => {
    if (mode !== "nav") {
      setMode("nav")
      // Record the downgrade timestamp so the `fullscreenchange` listener
      // can distinguish "user Esc'd out of fullscreen" (should exit) from
      // "browser intercepted our Esc and exited fullscreen as a side-effect
      // of our draw-mode downgrade" (should stay mounted).
      escDowngradeAtRef.current = Date.now()
      return
    }
    onExit()
  }, [mode, onExit])

  // ---- Keyboard ----------------------------------------------------------

  usePresentationKeyboard({
    enabled: true,
    onBlank: toggleBlank,
    onClearStrokes: handleClearStrokes,
    onEnd: goEnd,
    onExit: handleExitKey,
    onHome: goHome,
    onJump: jumpTo,
    onNext: goNext,
    onPrev: goPrev,
    onToggleLaser: toggleLaser,
    onTogglePen: togglePen,
    onToggleThumbs: toggleThumbs,
    onUndoStroke: handleUndoStroke,
  })

  // ---- Fullscreen exit on unmount ---------------------------------------
  //
  // Entry is handled by SlideSensePage's click handler (it calls
  // `requestFullscreen()` synchronously on the portal host before we even
  // mount, so the user-gesture token is still alive). We only own exit.

  useEffect(() => {
    return () => {
      try {
        // Guard: only call exitFullscreen when we're still in fullscreen.
        // The `fullscreenchange` listener below may have fired first when
        // the user exits via browser chrome, in which case the element is
        // already null and a second exit would be a no-op warning.
        if (typeof document !== "undefined" && document.fullscreenElement) {
          void document.exitFullscreen()
        }
      } catch {
        // Some browsers reject exitFullscreen outside of a user gesture —
        // ignore; the overlay is unmounted anyway.
      }
    }
  }, [])

  // ---- Sync overlay with external fullscreen exit -----------------------
  //
  // Single-window flow only: when the user hits Esc / F11 / browser chrome
  // to leave fullscreen, our React state stays `isPresenting=true`, which
  // would leave a black z-9999 overlay covering the page. Listen for
  // `fullscreenchange` and call `onExit` when we're no longer the
  // fullscreen element.
  //
  // The dual-window (presenter-view) path deliberately opts OUT of this:
  // the audience window's fullscreen there is inherently unstable. The
  // presenter popup steals OS focus the instant it opens — and when the
  // app runs as an installed PWA, that popup is a whole new app window that
  // can take seconds to appear, dropping the audience out of fullscreen
  // long after mount. Tearing the presentation down on that fullscreen loss
  // would return the audience window to the library and orphan the
  // presenter window forever on "Waiting for presentation…" (#2235). The
  // overlay covers the viewport via display:block regardless of native
  // fullscreen, so losing it is purely cosmetic (OS chrome reappears). On
  // this path exit is explicit only — audience Esc / toolbar Exit, or the
  // presenter's `end` message — none of which depend on `fullscreenchange`.
  // (The old time-boxed startup grace was a band-aid for the same race; an
  // installed PWA simply blew past its 1s window.)

  useEffect(() => {
    // Dual-window path: never auto-exit on fullscreen loss (see above).
    if (sessionId !== null) return

    const onFullscreenChange = () => {
      if (typeof document !== "undefined" && !document.fullscreenElement) {
        // The browser may have exited fullscreen as a side-effect of the
        // same Esc keypress that `handleExitKey` just used to downgrade
        // pen/laser back to nav. Preserve the 2-step Esc priority by
        // ignoring this change when it arrives within 300ms of a
        // downgrade — the user's next Esc (or the toolbar Exit button)
        // will close the presentation.
        if (Date.now() - escDowngradeAtRef.current < 300) {
          return
        }
        onExit()
      }
    }
    document.addEventListener("fullscreenchange", onFullscreenChange)
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange)
  }, [onExit, sessionId])

  // ---- Dual-window sync (master side) -----------------------------------
  //
  // The master (audience) window is the source of truth for slide
  // index + mode. On mount we publish an `init` so the presenter
  // window can hydrate; on every subsequent change we publish a
  // lighter-weight `state` update. Jump requests coming back from the
  // presenter are applied locally — which in turn triggers the state
  // broadcast below.

  const syncMode: PresentationSyncMode = isBlank ? "blank" : mode

  // Initial hello — run once on mount (or when the sync channel itself
  // is replaced, e.g. sessionId changes). Deps are intentionally
  // narrow: re-publishing `init` on every index/mode change would
  // break the "initial hydration" semantics on the presenter side.
  // Capture initial values in refs so the init-effect has a stable
  // dependency list (just `sync`). Incremental updates flow through
  // the state-broadcast effect below.
  const initSlidesRef = useRef(slides)
  const initIndexRef = useRef(safeInitial)
  const initModeRef = useRef<PresentationSyncMode>(syncMode)
  useEffect(() => {
    sync.publish({
      type: "init",
      slides: initSlidesRef.current,
      currentIndex: initIndexRef.current,
      mode: initModeRef.current,
      lastViewedIndex: lastViewedIndexRef.current,
    })
  }, [sync])

  // Incremental state broadcasts — sent on every slide change + mode
  // change (including toggling blank). Skips the first render because
  // `init` already carried the initial state.
  const didInitialBroadcastRef = useRef(false)
  useEffect(() => {
    if (!didInitialBroadcastRef.current) {
      didInitialBroadcastRef.current = true
      return
    }
    sync.publish({
      type: "state",
      currentIndex,
      mode: syncMode,
      lastViewedIndex: lastViewedIndexRef.current,
    })
  }, [sync, currentIndex, syncMode])

  // Track the latest slides / index / mode in refs so the `hello`
  // handler below can re-broadcast a *fresh* init without pulling
  // stale values from the subscribe closure. BroadcastChannel does
  // not buffer messages, so if the presenter window hasn't subscribed
  // by the time our mount-effect fires, the presenter sends a `hello`
  // on its own mount and we respond with the current state.
  const latestSlidesRef = useRef(slides)
  const latestIndexRef = useRef(currentIndex)
  const latestModeRef = useRef<PresentationSyncMode>(syncMode)
  useEffect(() => {
    latestSlidesRef.current = slides
  }, [slides])
  useEffect(() => {
    latestIndexRef.current = currentIndex
  }, [currentIndex])
  useEffect(() => {
    latestModeRef.current = syncMode
  }, [syncMode])

  // Subscribe to presenter-originated events.
  useEffect(() => {
    const unsubscribe = sync.subscribe((msg) => {
      switch (msg.type) {
        case "hello":
          // Presenter announced it's subscribed — re-broadcast a fresh
          // init with current state so it can hydrate. Respond on every
          // hello (no suppression) so re-opening the presenter window
          // re-establishes state cleanly.
          sync.publish({
            type: "init",
            slides: latestSlidesRef.current,
            currentIndex: latestIndexRef.current,
            mode: latestModeRef.current,
            lastViewedIndex: lastViewedIndexRef.current,
          })
          return
        case "jump":
          if (slides.length === 0) return
          lastViewedIndexRef.current = msg.lastViewedIndex
          setIsBlank(false)
          setCurrentIndex(Math.max(0, Math.min(msg.index, slides.length - 1)))
          return
        case "closed":
          setIsPresenterClosed(true)
          return
        // --- Presenter-driven tool mirroring (#2153) ---------------------
        case "laserMove":
          setRemoteLaser({ x: msg.x, y: msg.y })
          return
        case "laserOff":
          setRemoteLaser(null)
          return
        case "stroke":
          addStrokeForSlide(msg.slideId, msg.stroke)
          return
        case "clearStrokes":
          clearStrokesForSlide(msg.slideId)
          return
        case "undoStroke":
          undoStrokeForSlide(msg.slideId)
          return
        case "blank":
          setIsBlank(msg.blank)
          return
        case "end":
          // Presenter pressed Exit — tear the whole presentation down.
          onExit()
          return
        default:
          return
      }
    })
    return unsubscribe
  }, [sync, slides.length, addStrokeForSlide, clearStrokesForSlide, undoStrokeForSlide, onExit])

  // Drop the mirrored laser dot when the slide changes so it never
  // lingers on the new slide pointing at the wrong place.
  useEffect(() => {
    setRemoteLaser(null)
  }, [currentIndex])

  // Announce we're shutting down so the presenter window can switch
  // to its "presentation slutade" indicator. Two paths:
  //   1. React unmount cleanup (covers navigating away in-app, the
  //      common case). Only fires when the component unmounts cleanly.
  //   2. `pagehide` / `beforeunload` listener — covers hard tab-close,
  //      browser-kill, and refresh, where React's cleanup never runs.
  //      We mirror the presenter's pattern (PresenterView.tsx) so both
  //      sides behave symmetrically.
  useEffect(() => {
    const announceClosed = () => {
      sync.publish({ type: "closed" })
    }
    window.addEventListener("pagehide", announceClosed)
    window.addEventListener("beforeunload", announceClosed)
    return () => {
      window.removeEventListener("pagehide", announceClosed)
      window.removeEventListener("beforeunload", announceClosed)
      // React unmount path — also announce here so navigating away
      // in-app (no page-hide) still notifies the presenter.
      sync.publish({ type: "closed" })
    }
  }, [sync])

  // ---- Resolve current slide image URL ----------------------------------
  // (currentSlideId is computed earlier for the annotation store.)
  //
  // Load via the auth-aware blob hook, not a plain `<img src>` — the
  // Bearer token doesn't ride on resource loads in local dev, so a direct
  // URL 401s (#2153).
  const { url: currentUrl, error: imageError } = useSlideImageBlob(currentSlideId)

  // ---- Preload next 2 slides --------------------------------------------
  //
  // Warm the blob cache for the upcoming slides (same auth-aware fetch) so
  // advancing is instant. `new Image()` can't be used — it wouldn't carry
  // the Bearer token either.

  useEffect(() => {
    if (slides.length === 0) return
    const preloadIndexes = [currentIndex + 1, currentIndex + 2].filter((i) => i < slides.length)
    for (const i of preloadIndexes) {
      prefetchSlideImageBlob(slides[i]?.slide.id)
    }
  }, [currentIndex, slides])

  // ---- Overlay auto-hide -------------------------------------------------
  //
  // Bottom toolbar auto-hides after inactivity; re-shows on mousemove.
  // `kickOverlay` is called on mount and on every `mousemove` inside
  // the container.

  const kickOverlay = useCallback(() => {
    setIsOverlayVisible(true)
    if (overlayHideTimer.current !== null) clearTimeout(overlayHideTimer.current)
    overlayHideTimer.current = setTimeout(() => {
      setIsOverlayVisible(false)
    }, OVERLAY_HIDE_DELAY_MS)
  }, [])

  useEffect(() => {
    kickOverlay()
    return () => {
      if (overlayHideTimer.current !== null) clearTimeout(overlayHideTimer.current)
    }
  }, [kickOverlay])

  // ---- First-run hint ----------------------------------------------------
  //
  // Shown on mount; fades out after 3s.  `pointer-events-none` on the
  // wrapper ensures it never blocks clicks-to-advance.

  useEffect(() => {
    hintHideTimer.current = setTimeout(() => {
      setIsHintVisible(false)
    }, HINT_HIDE_DELAY_MS)
    return () => {
      if (hintHideTimer.current !== null) clearTimeout(hintHideTimer.current)
    }
  }, [])

  // ---- Click to advance --------------------------------------------------
  //
  // Click-to-next is only active in nav-mode.  In laser/pen mode the
  // click belongs to the overlay (laser consumes it as a non-event; the
  // annotation canvas captures pointerdown directly on itself).

  const handleClick = useCallback(() => {
    if (mode !== "nav") return
    goNext()
  }, [goNext, mode])

  // ---- Toolbar button handlers (stopPropagation so they don't ----------
  // ----- bubble up and trigger click-to-next) ----------------------------

  const handleToolbarPrev = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      goPrev()
    },
    [goPrev]
  )

  const handleToolbarNext = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      goNext()
    },
    [goNext]
  )

  const handleToolbarExit = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      onExit()
    },
    [onExit]
  )

  const handleToolbarToggleThumbs = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      toggleThumbs()
    },
    [toggleThumbs]
  )

  const handleToolbarToggleLaser = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      toggleLaser()
    },
    [toggleLaser]
  )

  const handleToolbarTogglePen = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      togglePen()
    },
    [togglePen]
  )

  const handleToolbarClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      clearCurrentStrokes()
    },
    [clearCurrentStrokes]
  )

  const handleToolbarUndo = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      undoLastStroke()
    },
    [undoLastStroke]
  )

  // ---- Render ------------------------------------------------------------

  if (slides.length === 0) return null

  const current = slides[currentIndex].slide
  const slideAlt =
    current.ai_title || t("slideSense.present.slideFallback", { n: currentIndex + 1 })
  const totalLabel = t("slideSense.present.slideCounter", {
    current: currentIndex + 1,
    total: slides.length,
  })

  // When laser is active the native cursor over the whole presentation
  // overlay is hidden (so only the red dot is visible).
  const containerStyle = mode === "laser" ? ({ cursor: "none" } as const) : undefined

  return (
    /* The presentation root needs to be a focusable region that also
       captures click / mousemove / keydown so the overlay UX works.
       role="region" + tabIndex + handlers is the correct a11y shape
       here (landmark that is reachable for keyboard users); silence the
       two jsx-a11y rules that flag the combination. */
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
    /* TooltipProvider is also mounted by app-provider, but we re-declare it here
       so the presentation overlay is self-contained when rendered into a
       portal host outside the provider tree (and so unit tests don't need
       to wrap each render). Nesting providers is supported by Radix. */
    <TooltipProvider delayDuration={400}>
      <div
        ref={rootRef}
        aria-label={t("slideSense.present.presentationRegion")}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black"
        data-testid="presentation-mode"
        role="region"
        style={containerStyle}
        tabIndex={0}
        onClick={handleClick}
        onMouseMove={kickOverlay}
        onKeyDown={(e) => {
          // Space/Enter on the focused container would also trigger the
          // click handler — keyboard advance is owned by
          // usePresentationKeyboard, so swallow here to avoid double-fire.
          if (e.key === " " || e.key === "Enter") e.preventDefault()
        }}
      >
        {isBlank && <div className="h-full w-full bg-black" data-testid="presentation-blank" />}
        {!isBlank && (
          <SlideStage aspect={aspect} className="absolute inset-0" data-testid="presentation-stage">
            {currentUrl && (
              <img
                key={current.id}
                alt={slideAlt}
                className="absolute inset-0 h-full w-full object-contain"
                data-testid="presentation-slide-image"
                src={currentUrl}
                onLoad={onImgLoad}
              />
            )}
            <SlideLinkHotspots
              enabled={mode === "nav"}
              jumpLabel={(n) => t("slideSense.present.jumpToSlide", { n })}
              lastViewedLabel={t("slideSense.present.returnToLastSlide")}
              links={current.links ?? []}
              onActivate={activateHotspot}
            />

            {/* Failed load — surface a message so the room can tell a real
              blank from an image that didn't load (e.g. a token rotating
              mid-talk → 401), mirroring the presenter view (#2153 review). */}
            {!currentUrl && imageError && (
              <div
                className="absolute inset-0 flex items-center justify-center text-sm text-white/80"
                data-testid="presentation-image-unavailable"
                role="status"
              >
                {t("slideSense.present.imageUnavailable")}
              </div>
            )}

            {/* Annotation canvas — absolute overlay over the slide stage.
              Always mounted so strokes from previous pen sessions render
              when returning to a slide; pointer events are only captured
              while mode === "pen" (audience-local drawing). Strokes streamed
              from the presenter window land in the same store and render here
              too — the stage makes the coordinates line up across windows. */}
            {currentSlideId && (
              <AnnotationCanvas
                active={mode === "pen"}
                slideId={currentSlideId}
                strokes={currentStrokes}
                onAddStroke={addStroke}
              />
            )}

            {/* Mirrored laser dot driven by the presenter window (#2153). */}
            {sessionId !== null && remoteLaser && (
              <StageLaserDot x={remoteLaser.x} y={remoteLaser.y} />
            )}
          </SlideStage>
        )}

        {/* Laser pointer — single-window only. In the dual-window flow the
          presenter drives the laser via `remoteLaser` (rendered inside the
          stage above); the local mouse-following dot would be a duplicate
          (nobody is at the audience screen anyway). */}
        <LaserPointer active={mode === "laser" && sessionId === null} />

        {/* Presenter-window-closed indicator — only shown when we opened
          a presenter window and it has since closed. Compact badge at
          the top-right, does not block interaction. */}
        {isPresenterClosed && (
          <div
            className="pointer-events-none absolute top-4 right-4 rounded-md bg-black/60 px-3 py-1.5 text-sm text-white backdrop-blur"
            data-testid="presentation-presenter-closed"
            role="status"
          >
            {t("slideSense.present.presenterViewClosed")}
          </div>
        )}

        <div
          className="pointer-events-none absolute top-4 left-4 z-[70] rounded-md bg-black/60 px-2.5 py-1 text-[11px] font-semibold tracking-wider text-white uppercase backdrop-blur"
          data-testid="presentation-preview-mode-chip"
        >
          {t("slideSense.present.previewModeChip")}
        </div>

        {/* First-run hint — top-center, fades after 3s. pointer-events-none
          so it never blocks the click-to-advance handler on the container. */}
        <div
          aria-hidden={!isHintVisible}
          data-testid="presentation-hint"
          className={`pointer-events-none absolute top-8 left-1/2 -translate-x-1/2 rounded-md bg-black/60 px-4 py-2 text-lg text-white backdrop-blur transition-opacity duration-500 ${
            isHintVisible ? "opacity-100" : "opacity-0"
          }`}
        >
          {t("slideSense.present.exitHint")}
        </div>

        {/* Thumbnail strip — sits above the toolbar and follows the same
          auto-hide timer so audiences don't see persistent chrome.
          Clicks on thumbs stopPropagation internally to avoid
          triggering the container's click-to-next. */}
        <ThumbnailStrip
          currentIndex={currentIndex}
          slides={slides}
          visible={showThumbs && isOverlayVisible}
          onJump={jumpToIndex}
        />

        {/* Bottom toolbar — Grid · Prev · counter · Next · | · Exit.
          Auto-hides after 2s of no mousemove.  Button clicks stopPropagation
          so they don't bubble to the container's click-to-next handler.
          z-[60] so the toolbar sits above the AnnotationCanvas (zIndex 50)
          and stays clickable while pen mode is active. */}
        <div
          aria-hidden={!isOverlayVisible}
          data-testid="presentation-toolbar"
          className={`absolute bottom-4 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/60 px-2 py-1 backdrop-blur transition-opacity duration-200 ${
            isOverlayVisible ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={t("slideSense.present.toggleThumbnails")}
                aria-pressed={showThumbs}
                data-testid="presentation-toolbar-thumbs"
                size="icon"
                variant="ghost"
                className={`text-white hover:bg-white/10 hover:text-white ${
                  showThumbs ? "bg-white/10" : ""
                }`}
                onClick={handleToolbarToggleThumbs}
              >
                <LayoutGrid />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {t("slideSense.present.toggleThumbnails")}
              <kbd className="ml-2 rounded bg-white/10 px-1 text-xs">S</kbd>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={t("slideSense.present.modeLaser")}
                aria-pressed={mode === "laser"}
                data-testid="presentation-toolbar-laser"
                size="icon"
                variant="ghost"
                className={`text-white hover:bg-white/10 hover:text-white ${
                  mode === "laser" ? "bg-white/10" : ""
                }`}
                onClick={handleToolbarToggleLaser}
              >
                <Pointer />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {t("slideSense.present.modeLaser")}
              <kbd className="ml-2 rounded bg-white/10 px-1 text-xs">L</kbd>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={t("slideSense.present.modePen")}
                aria-pressed={mode === "pen"}
                data-testid="presentation-toolbar-pen"
                size="icon"
                variant="ghost"
                className={`text-white hover:bg-white/10 hover:text-white ${
                  mode === "pen" ? "bg-white/10" : ""
                }`}
                onClick={handleToolbarTogglePen}
              >
                <Pen />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {t("slideSense.present.modePen")}
              <kbd className="ml-2 rounded bg-white/10 px-1 text-xs">D</kbd>
            </TooltipContent>
          </Tooltip>
          {mode === "pen" && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={t("slideSense.present.undoStroke")}
                    className="text-white hover:bg-white/10 hover:text-white"
                    data-testid="presentation-toolbar-undo"
                    size="icon"
                    variant="ghost"
                    onClick={handleToolbarUndo}
                  >
                    <Undo2 />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {t("slideSense.present.undoStroke")}
                  <kbd className="ml-2 rounded bg-white/10 px-1 text-xs">Ctrl+Z</kbd>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={t("slideSense.present.clearStrokes")}
                    className="text-white hover:bg-white/10 hover:text-white"
                    data-testid="presentation-toolbar-clear"
                    size="icon"
                    variant="ghost"
                    onClick={handleToolbarClear}
                  >
                    <Trash2 />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {t("slideSense.present.clearStrokes")}
                  <kbd className="ml-2 rounded bg-white/10 px-1 text-xs">E</kbd>
                </TooltipContent>
              </Tooltip>
            </>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={t("slideSense.present.prev")}
                className="text-white hover:bg-white/10 hover:text-white"
                data-testid="presentation-toolbar-prev"
                size="icon"
                variant="ghost"
                onClick={handleToolbarPrev}
              >
                <ChevronLeft />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {t("slideSense.present.prev")}
              <kbd className="ml-2 rounded bg-white/10 px-1 text-xs">←</kbd>
            </TooltipContent>
          </Tooltip>
          <span
            className="min-w-[4rem] text-center text-sm text-white/90"
            data-testid="presentation-progress"
          >
            {totalLabel}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={t("slideSense.present.next")}
                className="text-white hover:bg-white/10 hover:text-white"
                data-testid="presentation-toolbar-next"
                size="icon"
                variant="ghost"
                onClick={handleToolbarNext}
              >
                <ChevronRight />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {t("slideSense.present.next")}
              <kbd className="ml-2 rounded bg-white/10 px-1 text-xs">→</kbd>
            </TooltipContent>
          </Tooltip>
          <span aria-hidden className="mx-1 h-6 w-px bg-white/20" />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={t("slideSense.present.exitAria")}
                className="gap-2 text-white hover:bg-white/10 hover:text-white"
                data-testid="presentation-toolbar-exit"
                size="sm"
                variant="ghost"
                onClick={handleToolbarExit}
              >
                <X />
                <span className="text-sm">{t("slideSense.present.exit")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {t("slideSense.present.exitAria")}
              <kbd className="ml-2 rounded bg-white/10 px-1 text-xs">Esc</kbd>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Screen-reader-only announcer for slide progress.
          Kept OUTSIDE the toolbar because the toolbar is `aria-hidden`
          while auto-hidden, which would silence the live region. */}
        <span
          aria-atomic="true"
          aria-live="polite"
          className="sr-only"
          data-testid="presentation-progress-announcer"
        >
          {totalLabel}
        </span>
      </div>
    </TooltipProvider>
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
  )
}
